import $ from 'jquery';

const SVG_NS = 'http://www.w3.org/2000/svg';
let instanceCounter = 0;

function debounce(fn, wait) {
    let timer = null;
    return function debounced(...args) {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => fn.apply(this, args), wait);
    };
}

function stripSoftHyphens(value) {
    return value.replace(/­/g, '');
}

export default class Organigramm {
    constructor(root) {
        this.uid = ++instanceCounter;
        this.root = root;
        this.$root = $(root);

        this.stage = root.querySelector('.organigramm__stage');
        this.canvas = root.querySelector('.organigramm__canvas');
        this.svg = root.querySelector('.organigramm__lines');
        this.contents = root.querySelector('.organigramm__contents');
        this.modal = root.querySelector('.organigramm__modal');

        if (!this.stage || !this.canvas || !this.svg || !this.modal) {
            return;
        }

        this.modalBody = this.modal.querySelector('.organigramm__modal-body');
        this.modalTitle = this.modal.querySelector('.organigramm__modal-title');
        this.modalClose = this.modal.querySelector('.organigramm__modal-close');

        this.lastFocused = null;
        this.modalKeyHandler = (event) => this.onModalKey(event);

        this.createTooltip();
        this.collectNodes();
        this.collectConnections();
        this.bindEvents();
        this.runEntrance();
    }

    createTooltip() {
        this.tooltip = document.createElement('div');
        this.tooltip.className = 'organigramm__tooltip';
        this.tooltip.setAttribute('role', 'tooltip');
        this.tooltip.setAttribute('aria-hidden', 'true');
        this.root.appendChild(this.tooltip);
        this.$tooltip = $(this.tooltip);
    }

    collectNodes() {
        this.nodeEls = {};
        this.nodeTitles = {};

        this.$root.find('.organigramm__node').each((index, el) => {
            const id = el.dataset.id;
            this.nodeEls[id] = el;
            this.nodeTitles[id] = stripSoftHyphens($(el).find('.organigramm__node-title').text().trim());
        });
    }

    collectConnections() {
        this.connections = [];
        this.parents = {};

        this.$root.find('.organigramm__node').each((index, el) => {
            const id = el.dataset.id;

            const addEdge = (from, type) => {
                if (!from) {
                    return;
                }
                this.connections.push({ from, to: id, type: type || 'bericht' });
                if (!this.parents[id]) {
                    this.parents[id] = [];
                }
                this.parents[id].push(from);
            };

            addEdge(el.dataset.from, el.dataset.type);
            addEdge(el.dataset.from2, el.dataset.type2);
            addEdge(el.dataset.from3, el.dataset.type3);
        });
    }

    bindEvents() {
        this.$root
            .on('mouseenter focusin', '.organigramm__node', (event) => {
                this.highlight(event.currentTarget.dataset.id);
            })
            .on('mouseleave focusout', '.organigramm__node', () => {
                this.clearHighlight();
            })
            .on('click', '.organigramm__node', (event) => {
                this.openModal(event.currentTarget.dataset.id, event.currentTarget);
            })
            .on('click', '.organigramm__modal-close', () => {
                this.closeModal();
            })
            .on('click', '.organigramm__modal-backdrop', () => {
                this.closeModal();
            });

        const redraw = debounce(() => this.drawLines(), 80);
        window.addEventListener('resize', redraw);

        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => this.drawLines());
        }

        if (window.ResizeObserver) {
            let lastWidth = 0;
            let lastHeight = 0;
            const observer = new ResizeObserver(debounce(() => {
                const rect = this.canvas.getBoundingClientRect();
                if (Math.abs(rect.width - lastWidth) < 1 && Math.abs(rect.height - lastHeight) < 1) {
                    return;
                }
                lastWidth = rect.width;
                lastHeight = rect.height;
                this.drawLines();
            }, 80));
            observer.observe(this.canvas);
        }
    }

    runEntrance() {
        this.drawLines();
        void this.stage.offsetWidth;
        window.setTimeout(() => {
            this.stage.classList.add('organigramm__stage--entered');
            this.drawLines();
        }, 20);
    }

    buildLayers(canvasRect) {
        this.layers = [];
        this.nodeLayer = {};
        this.rectById = {};
        this.branchMinTop = {};

        this.canvas.querySelectorAll('.organigramm__node').forEach((nodeEl) => {
            const r = nodeEl.getBoundingClientRect();
            const rect = {
                left: r.left - canvasRect.left,
                right: r.right - canvasRect.left,
                top: r.top - canvasRect.top,
                bottom: r.bottom - canvasRect.top,
                cx: r.left + r.width / 2 - canvasRect.left,
                cy: r.top + r.height / 2 - canvasRect.top,
                branch: nodeEl.dataset.branch || '',
            };
            this.rectById[nodeEl.dataset.id] = rect;
            if (rect.branch && (this.branchMinTop[rect.branch] === undefined || rect.top < this.branchMinTop[rect.branch])) {
                this.branchMinTop[rect.branch] = rect.top;
            }
        });

        this.canvas.querySelectorAll('.organigramm__layer').forEach((layerEl, layerIndex) => {
            const rects = [];
            let top = Infinity;
            let bottom = -Infinity;

            layerEl.querySelectorAll('.organigramm__node').forEach((nodeEl) => {
                const rect = this.rectById[nodeEl.dataset.id];
                top = Math.min(top, rect.top);
                bottom = Math.max(bottom, rect.bottom);
                if (nodeEl.dataset.branch) {
                    return;
                }
                rects.push(rect);
                this.nodeLayer[nodeEl.dataset.id] = layerIndex;
            });

            rects.sort((a, b) => a.left - b.left);
            this.layers.push({ top, bottom, rects });
        });
    }

    isBranchHead(id) {
        const rect = this.rectById[id];
        if (!rect || !rect.branch) {
            return false;
        }
        return rect.top <= this.branchMinTop[rect.branch] + 2;
    }

    verticalPoints(from, to) {
        const midY = (from.bottom + to.top) / 2;
        return [
            [from.cx, from.bottom],
            [from.cx, midY],
            [to.cx, midY],
            [to.cx, to.top],
        ];
    }

    sidePoints(from, to, fromLayer) {
        const gap = 14;
        const hasGap = fromLayer !== undefined && this.layers[fromLayer] && this.layers[fromLayer + 1];
        const busY = hasGap ? this.gapY(fromLayer) : (from.bottom + to.top) / 2;
        const preferX = from.cx >= to.cx ? to.right + gap : to.left - gap;
        const channelX = this.findTrunkX(busY, to.cy, preferX);
        const targetX = channelX >= to.cx ? to.right : to.left;
        return [
            [from.cx, from.bottom],
            [from.cx, busY],
            [channelX, busY],
            [channelX, to.cy],
            [targetX, to.cy],
        ];
    }

    gapY(index) {
        return (this.layers[index].bottom + this.layers[index + 1].top) / 2;
    }

    isChannelClear(x, yTop, yBottom) {
        const padding = 4;
        for (const id in this.rectById) {
            const rect = this.rectById[id];
            if (rect.bottom > yTop + 1 && rect.top < yBottom - 1 && x > rect.left - padding && x < rect.right + padding) {
                return false;
            }
        }
        return true;
    }

    findTrunkX(yTop, yBottom, preferX) {
        if (this.isChannelClear(preferX, yTop, yBottom)) {
            return preferX;
        }

        const margin = 24;
        const rects = [];
        for (const id in this.rectById) {
            const rect = this.rectById[id];
            if (rect.bottom > yTop + 1 && rect.top < yBottom - 1) {
                rects.push(rect);
            }
        }
        rects.sort((a, b) => a.left - b.left);

        const candidates = [];
        let minLeft = Infinity;
        let maxRight = -Infinity;
        for (let k = 0; k < rects.length - 1; k++) {
            candidates.push((rects[k].right + rects[k + 1].left) / 2);
        }
        rects.forEach((rect) => {
            minLeft = Math.min(minLeft, rect.left);
            maxRight = Math.max(maxRight, rect.right);
        });
        candidates.push(minLeft - margin, maxRight + margin);

        let best = preferX;
        let bestDistance = Infinity;
        candidates.forEach((x) => {
            if (this.isChannelClear(x, yTop, yBottom)) {
                const distance = Math.abs(x - preferX);
                if (distance < bestDistance) {
                    bestDistance = distance;
                    best = x;
                }
            }
        });
        return best;
    }

    connectionPoints(connection) {
        const from = this.rectById[connection.from];
        const to = this.rectById[connection.to];
        if (!from || !to) {
            return null;
        }

        if (to.branch && from.branch === to.branch) {
            return this.verticalPoints(from, to);
        }
        if (to.branch) {
            return this.isBranchHead(connection.to) ? this.verticalPoints(from, to) : this.sidePoints(from, to, this.nodeLayer[connection.from]);
        }
        if (from.branch) {
            return this.verticalPoints(from, to);
        }

        const fromLayer = this.nodeLayer[connection.from];
        const toLayer = this.nodeLayer[connection.to];

        if (fromLayer === toLayer) {
            const lo = Math.min(from.cx, to.cx);
            const hi = Math.max(from.cx, to.cx);
            const nodeBetween = this.layers[fromLayer].rects.some((rect) => rect.cx > lo + 1 && rect.cx < hi - 1);

            if (!nodeBetween) {
                const y = (from.cy + to.cy) / 2;
                if (to.cx > from.cx) {
                    return [[from.right, y], [to.left, y]];
                }
                return [[from.left, y], [to.right, y]];
            }

            const aboveY = fromLayer > 0 ? this.gapY(fromLayer - 1) : Math.min(from.top, to.top) - 20;
            return [
                [from.cx, from.top],
                [from.cx, aboveY],
                [to.cx, aboveY],
                [to.cx, to.top],
            ];
        }

        const gapTop = this.gapY(fromLayer);
        const gapBottom = this.gapY(toLayer - 1);
        let trunkX = to.cx;
        if (toLayer - 1 >= fromLayer + 1) {
            trunkX = this.findTrunkX(gapTop, gapBottom, from.cx);
        }

        return [
            [from.cx, from.bottom],
            [from.cx, gapTop],
            [trunkX, gapTop],
            [trunkX, gapBottom],
            [to.cx, gapBottom],
            [to.cx, to.top],
        ];
    }

    roundedPath(rawPoints, radius) {
        const deduped = [];
        rawPoints.forEach((point) => {
            const last = deduped[deduped.length - 1];
            if (!last || Math.abs(last[0] - point[0]) > 0.5 || Math.abs(last[1] - point[1]) > 0.5) {
                deduped.push(point);
            }
        });

        const points = [];
        deduped.forEach((point, i) => {
            if (i === 0 || i === deduped.length - 1) {
                points.push(point);
                return;
            }
            const a = deduped[i - 1];
            const c = deduped[i + 1];
            const cross = (point[0] - a[0]) * (c[1] - a[1]) - (point[1] - a[1]) * (c[0] - a[0]);
            if (Math.abs(cross) > 0.5) {
                points.push(point);
            }
        });

        if (points.length < 2) {
            return '';
        }
        if (points.length === 2) {
            return `M ${points[0][0]} ${points[0][1]} L ${points[1][0]} ${points[1][1]}`;
        }

        let d = `M ${points[0][0]} ${points[0][1]}`;
        for (let i = 1; i < points.length - 1; i++) {
            const a = points[i - 1];
            const b = points[i];
            const c = points[i + 1];
            const lenAB = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
            const lenBC = Math.hypot(c[0] - b[0], c[1] - b[1]) || 1;
            const r = Math.min(radius, lenAB / 2, lenBC / 2);
            const p1x = b[0] - ((b[0] - a[0]) / lenAB) * r;
            const p1y = b[1] - ((b[1] - a[1]) / lenAB) * r;
            const p2x = b[0] + ((c[0] - b[0]) / lenBC) * r;
            const p2y = b[1] + ((c[1] - b[1]) / lenBC) * r;
            d += ` L ${p1x} ${p1y} Q ${b[0]} ${b[1]} ${p2x} ${p2y}`;
        }
        const last = points[points.length - 1];
        d += ` L ${last[0]} ${last[1]}`;
        return d;
    }

    drawLines() {
        const canvasRect = this.canvas.getBoundingClientRect();
        this.svg.setAttribute('viewBox', `0 0 ${canvasRect.width} ${canvasRect.height}`);

        while (this.svg.firstChild) {
            this.svg.removeChild(this.svg.firstChild);
        }

        const markerId = `organigramm-arrow-${this.uid}`;
        const defs = document.createElementNS(SVG_NS, 'defs');
        defs.innerHTML =
            `<marker id="${markerId}" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto" markerUnits="strokeWidth">` +
            '<path d="M 0 0 L 10 5 L 0 10 z" class="organigramm__arrow-head theme-rose"/>' +
            '</marker>';
        this.svg.appendChild(defs);

        this.buildLayers(canvasRect);

        this.connections.forEach((connection) => {
            const points = this.connectionPoints(connection);
            if (!points) {
                return;
            }
            const pathData = this.roundedPath(points, 10);
            if (!pathData) {
                return;
            }

            const hit = document.createElementNS(SVG_NS, 'path');
            hit.setAttribute('d', pathData);
            hit.setAttribute('class', 'organigramm__line-hit');
            hit.dataset.from = connection.from;
            hit.dataset.to = connection.to;
            hit.addEventListener('mouseenter', (event) => this.showTooltip(connection, event));
            hit.addEventListener('mousemove', (event) => this.positionTooltip(event));
            hit.addEventListener('mouseleave', () => this.hideTooltip());
            this.svg.appendChild(hit);

            const line = document.createElementNS(SVG_NS, 'path');
            line.setAttribute('d', pathData);
            const themeClass = connection.type === 'wahl' ? ' theme-rose' : '';
            line.setAttribute('class', `organigramm__line organigramm__line--${connection.type}${themeClass}`);
            line.dataset.from = connection.from;
            line.dataset.to = connection.to;
            if (connection.type === 'wahl') {
                line.setAttribute('marker-end', `url(#${markerId})`);
            }
            this.svg.appendChild(line);
        });
    }

    collectAncestors(id, collected) {
        const parents = this.parents[id];
        if (!parents) {
            return;
        }
        parents.forEach((parentId) => {
            if (!collected.has(parentId)) {
                collected.add(parentId);
                this.collectAncestors(parentId, collected);
            }
        });
    }

    collectDescendants(id, collected) {
        const parentEl = this.nodeEls[id];
        const parentBranch = parentEl ? parentEl.dataset.branch : '';
        this.connections.forEach((connection) => {
            if (connection.from === id && !collected.has(connection.to)) {
                collected.add(connection.to);
                const childEl = this.nodeEls[connection.to];
                const childBranch = childEl ? childEl.dataset.branch : '';
                if (childBranch && childBranch === parentBranch) {
                    this.collectAncestors(connection.to, collected);
                    this.collectDescendants(connection.to, collected);
                }
            }
        });
    }

    highlight(id) {
        this.stage.classList.add('organigramm__stage--highlighting');

        const connected = new Set([id]);
        this.collectAncestors(id, connected);
        this.collectDescendants(id, connected);

        Object.keys(this.nodeEls).forEach((nodeId) => {
            const el = this.nodeEls[nodeId];
            el.classList.toggle('organigramm__node--dim', !connected.has(nodeId));
            el.classList.toggle('organigramm__node--active', nodeId === id);
            el.classList.toggle('organigramm__node--connected', connected.has(nodeId) && nodeId !== id);
        });

        this.svg.querySelectorAll('.organigramm__line').forEach((line) => {
            const onPath = connected.has(line.dataset.from) && connected.has(line.dataset.to);
            line.classList.toggle('organigramm__line--dim', !onPath);
            line.classList.toggle('organigramm__line--active', onPath);
        });
    }

    clearHighlight() {
        this.stage.classList.remove('organigramm__stage--highlighting');

        Object.keys(this.nodeEls).forEach((nodeId) => {
            this.nodeEls[nodeId].classList.remove(
                'organigramm__node--dim',
                'organigramm__node--active',
                'organigramm__node--connected'
            );
        });

        this.svg.querySelectorAll('.organigramm__line').forEach((line) => {
            line.classList.remove('organigramm__line--dim', 'organigramm__line--active');
        });
    }

    showTooltip(connection, event) {
        const fromTitle = this.nodeTitles[connection.from] || '';
        const toTitle = this.nodeTitles[connection.to] || '';

        let typeLabel = 'Berichtsbeziehung';
        let text = `${toTitle} berichtet an / wird unterstützt von ${fromTitle}`;
        if (connection.type === 'wahl') {
            typeLabel = 'Wahlbeziehung';
            text = `${fromTitle} wählt ${toTitle}`;
        }

        this.tooltip.innerHTML = '';
        const typeEl = document.createElement('span');
        typeEl.className = 'organigramm__tooltip-type';
        typeEl.textContent = typeLabel;
        this.tooltip.appendChild(typeEl);
        this.tooltip.appendChild(document.createTextNode(text));

        this.tooltip.classList.remove('theme-rose', 'theme-azure');
        this.tooltip.classList.add(connection.type === 'wahl' ? 'theme-rose' : 'theme-azure');
        this.tooltip.setAttribute('aria-hidden', 'false');

        this.positionTooltip(event);
        this.tooltip.classList.add('organigramm__tooltip--visible');
    }

    positionTooltip(event) {
        const padding = 14;
        const tooltipWidth = this.$tooltip.outerWidth();
        const tooltipHeight = this.$tooltip.outerHeight();

        let x = event.clientX + padding;
        let y = event.clientY + padding;

        if (x + tooltipWidth > window.innerWidth - 8) {
            x = event.clientX - tooltipWidth - padding;
        }
        if (y + tooltipHeight > window.innerHeight - 8) {
            y = event.clientY - tooltipHeight - padding;
        }

        this.tooltip.style.left = `${x}px`;
        this.tooltip.style.top = `${y}px`;
    }

    hideTooltip() {
        this.tooltip.classList.remove('organigramm__tooltip--visible');
        this.tooltip.setAttribute('aria-hidden', 'true');
    }

    openModal(id, trigger) {
        this.lastFocused = trigger;

        const title = trigger ? stripSoftHyphens($(trigger).find('.organigramm__node-title').text().trim()) : '';
        this.modalTitle.textContent = title;

        const block = this.contents ? this.contents.querySelector(`[data-content="${id}"]`) : null;
        if (block && block.innerHTML.trim()) {
            this.modalBody.innerHTML = block.innerHTML;
        } else {
            this.modalBody.innerHTML = '<p class="organigramm__modal-placeholder">Für diesen Knoten sind aktuell keine Inhalte hinterlegt.</p>';
        }

        this.modal.hidden = false;
        void this.modal.offsetWidth;
        this.modal.classList.add('organigramm__modal--open');
        this.modalClose.focus();

        document.addEventListener('keydown', this.modalKeyHandler);
    }

    closeModal() {
        this.modal.classList.remove('organigramm__modal--open');
        document.removeEventListener('keydown', this.modalKeyHandler);

        window.setTimeout(() => {
            this.modal.hidden = true;
            if (this.lastFocused) {
                this.lastFocused.focus();
            }
        }, 200);
    }

    onModalKey(event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.closeModal();
            return;
        }

        if (event.key !== 'Tab') {
            return;
        }

        const focusable = $(this.modal)
            .find('button, a[href], [tabindex]:not([tabindex="-1"])')
            .filter(':visible');
        if (!focusable.length) {
            return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }
}

$(() => {
    $('.organigramm').each((index, el) => new Organigramm(el));
});
